import { useState } from 'react';
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
    DragEndEvent,
} from '@dnd-kit/core';
import { restrictToVerticalAxis, restrictToParentElement } from '@dnd-kit/modifiers';
import { SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { zodResolver } from '@hookform/resolvers/zod';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import { Button, Form, Header } from 'semantic-ui-react';
import {
    saveQualityRequirementsPolicySchema,
    SaveQualityRequirementsPolicyType,
    QualityRequirementsPolicyType,
    ReviewBypassTierType,
} from '../../../../../schemas/qualityRequirementsPolicy.schemas';
import BypassTierItem from './components/bypassTierItem';
import './qualityRequirementsPolicyForm.css';

type QualityRequirementsPolicyFormPropsType = {
    policy?: QualityRequirementsPolicyType;
    onSave: (data: SaveQualityRequirementsPolicyType) => void;
    onCancel?: () => void;
};

const defaultTier: ReviewBypassTierType = {
    requirements: { minFullChecks: 0, minVisualChecks: 0, minCDChecks: 0 },
    maxUpdaterMistakesPer100: 0,
    maxFcReviewerMistakesPer100: 1,
    maxCdReviewerMistakesPer100: 1,
    mistakesIncreasePerReviewerPercent: 0,
    allowOnHistoricalCoreDataChange: false,
    allowOnHistoricalNonDataChange: false,
};

const defaultValues: SaveQualityRequirementsPolicyType = {
    tableName: '',
    minReviewRequirements: {
        minFullChecks: 0,
        minVisualChecks: 0,
        minCDChecks: 0,
    },
    bypassTiers: [],
};

function QualityRequirementsPolicyForm({ policy, onSave, onCancel }: QualityRequirementsPolicyFormPropsType) {
    const [expandedIndex, setExpandedIndex] = useState<number | null>(null);

    const {
        control,
        handleSubmit,
        formState: { errors, isSubmitting },
    } = useForm<SaveQualityRequirementsPolicyType>({
        defaultValues: policy
            ? {
                  tableName: policy.tableName,
                  minReviewRequirements: policy.minReviewRequirements,
                  bypassTiers: policy.bypassTiers,
              }
            : defaultValues,
        resolver: zodResolver(saveQualityRequirementsPolicySchema),
    });

    const { fields, append, remove, move } = useFieldArray({
        control,
        name: 'bypassTiers',
    });

    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
    );

    const handleDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;
        if (!over || active.id === over.id) {
            return;
        }

        const oldIndex = fields.findIndex((f) => f.id === active.id);
        const newIndex = fields.findIndex((f) => f.id === over.id);

        if (expandedIndex !== null) {
            if (expandedIndex === oldIndex) {
                setExpandedIndex(newIndex);
            } else if (oldIndex < expandedIndex && newIndex >= expandedIndex) {
                setExpandedIndex(expandedIndex - 1);
            } else if (oldIndex > expandedIndex && newIndex <= expandedIndex) {
                setExpandedIndex(expandedIndex + 1);
            }
        }

        move(oldIndex, newIndex);
    };

    const onSubmit = handleSubmit((data) => {
        onSave({ ...data, tableName: data.tableName.trim() });
    });

    return (
        <Form onSubmit={onSubmit}>
            <Controller
                name="tableName"
                control={control}
                render={({ field: { value, onChange } }) => (
                    <Form.Input
                        disabled={isSubmitting || policy?.deletable === false}
                        value={value}
                        onChange={(_, data) => onChange(data.value)}
                        fluid
                        label="Table Name"
                        placeholder="e.g. income_statement"
                        error={errors.tableName?.message}
                    />
                )}
            />

            <Header as="h4" dividing>
                Min Review Requirements
            </Header>

            <Form.Group widths="equal">
                <Controller
                    name="minReviewRequirements.minCDChecks"
                    control={control}
                    render={({ field: { value, onChange } }) => (
                        <Form.Input
                            disabled={isSubmitting}
                            value={value}
                            onChange={(_, data) => onChange(Number(data.value))}
                            type="number"
                            label="Min CD Checks"
                            error={errors.minReviewRequirements?.minCDChecks?.message}
                        />
                    )}
                />
                <Controller
                    name="minReviewRequirements.minFullChecks"
                    control={control}
                    render={({ field: { value, onChange } }) => (
                        <Form.Input
                            disabled={isSubmitting}
                            value={value}
                            onChange={(_, data) => onChange(Number(data.value))}
                            type="number"
                            label="Min Full Checks"
                            error={errors.minReviewRequirements?.minFullChecks?.message}
                        />
                    )}
                />
                <Controller
                    name="minReviewRequirements.minVisualChecks"
                    control={control}
                    render={({ field: { value, onChange } }) => (
                        <Form.Input
                            disabled={isSubmitting}
                            value={value}
                            onChange={(_, data) => onChange(Number(data.value))}
                            type="number"
                            label="Min Visual Checks"
                            error={errors.minReviewRequirements?.minVisualChecks?.message}
                        />
                    )}
                />
            </Form.Group>

            {errors.minReviewRequirements?.message && (
                <div className="ui pointing above prompt label">{errors.minReviewRequirements.message}</div>
            )}

            <Header as="h4" dividing>
                Bypass Tiers
                <Button
                    type="button"
                    size="mini"
                    primary
                    className="bypass-tiers__add-btn"
                    disabled={isSubmitting}
                    onClick={() => {
                        append(defaultTier);
                        setExpandedIndex(fields.length);
                    }}
                >
                    Add Tier
                </Button>
            </Header>

            <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                modifiers={[restrictToVerticalAxis, restrictToParentElement]}
                onDragEnd={handleDragEnd}
            >
                <SortableContext items={fields.map((f) => f.id)} strategy={verticalListSortingStrategy}>
                    {fields.map((field, index) => (
                        <BypassTierItem
                            key={field.id}
                            id={field.id}
                            control={control}
                            index={index}
                            isExpanded={expandedIndex === index}
                            isSubmitting={isSubmitting}
                            onToggle={() => setExpandedIndex(expandedIndex === index ? null : index)}
                            onRemove={() => {
                                remove(index);
                                if (expandedIndex === index) {
                                    setExpandedIndex(null);
                                } else if (expandedIndex !== null && expandedIndex > index) {
                                    setExpandedIndex(expandedIndex - 1);
                                }
                            }}
                            errors={errors}
                        />
                    ))}
                </SortableContext>
            </DndContext>

            <Form.Group className="flex flex-justify-space-between">
                <Form.Button primary type="submit" disabled={isSubmitting}>
                    Save
                </Form.Button>
                <Form.Button type="button" onClick={onCancel} disabled={isSubmitting}>
                    Cancel
                </Form.Button>
            </Form.Group>
        </Form>
    );
}

export default QualityRequirementsPolicyForm;
